import type { CapabilityGroupDef } from '@tandemone/types'
import { ChevronRight } from 'lucide-react'

import { Checkbox } from '@/components/ui/checkbox'

import { labelMatches } from './capabilities-derive'
import { CapabilityLeafRow } from './capability-leaf-row'
import { useCapabilitiesForm } from './use-capabilities-form'

/** a group: a tri-state "select all" checkbox + collapsible label with its
 *  selected-count, and the leaf rows (filtered by the search query). renders
 *  nothing when no leaf matches the query. */
export function CapabilityGroupSection({
  group,
  categoryLabel
}: {
  group: CapabilityGroupDef
  categoryLabel: string
}) {
  const { state, derived, dispatch } = useCapabilitiesForm()

  const visibleLeaves = group.items.filter(leaf =>
    labelMatches(state.query, leaf.label, group.label, categoryLabel)
  )
  if (visibleLeaves.length === 0) return null

  const total = group.items.length
  const count = derived.countsByGroup[group.key] ?? 0
  const checkboxState: boolean | 'indeterminate' =
    count === 0 ? false : count === total ? true : 'indeterminate'
  const open = state.query !== '' || (state.expandedGroups[group.key] ?? false)

  return (
    <div className='rounded-lg border border-border/60'>
      <div className='flex items-center gap-2 px-3 py-2'>
        <Checkbox
          checked={checkboxState}
          aria-label={`Select all in ${group.label}`}
          onCheckedChange={() =>
            dispatch({
              type: 'toggleGroupAll',
              groupKey: group.key,
              leafKeys: group.items.map(item => item.key),
              select: count < total
            })
          }
        />
        <button
          type='button'
          onClick={() => dispatch({ type: 'toggleGroup', groupKey: group.key })}
          className='flex min-w-0 flex-1 cursor-pointer items-center justify-between gap-2 text-left'
        >
          <span className='flex items-center gap-1.5 text-[13px] font-medium text-ink'>
            <ChevronRight
              className={`size-3.5 shrink-0 text-ink-muted transition-transform ${
                open ? 'rotate-90' : ''
              }`}
            />
            {group.label}
          </span>
          {count > 0 && (
            <span className='shrink-0 text-[11px] text-ink-muted'>
              {count} selected
            </span>
          )}
        </button>
      </div>
      {open && (
        <div className='flex flex-col gap-1 px-3 pb-3 pl-3 sm:pl-10'>
          {visibleLeaves.map(leaf => (
            <CapabilityLeafRow
              key={leaf.key}
              leafKey={leaf.key}
              label={leaf.label}
            />
          ))}
        </div>
      )}
    </div>
  )
}
